/** One browser/server controller. Y is up; position is avatar feet. Units are metres. */
export const MOVEMENT = Object.freeze({
  speed:5.8,sprintSpeed:9,jumpSpeed:7.8,radius:0.34,height:1.75,maxDt:0.1,maxStep:1/90,
  acceleration:35,deceleration:42,turnAcceleration:45,airAcceleration:10,airDeceleration:2,
  coyoteTime:0.09,jumpBufferTime:0.12,stepHeight:0.35,groundSnap:0.35,
  flightSpeed:12,flightBoostSpeed:18,flightVerticalSpeed:9,flightAcceleration:30,flightDeceleration:36,flightMaxHeight:80,
});
const clamp=(n,a,b)=>Math.min(b,Math.max(a,n));
const finite=(v,fallback=0)=>typeof v==='number'&&Number.isFinite(v)?v:fallback;
export const CITY_PLAYABLE_BOUNDS=Object.freeze({minX:-70,maxX:70,minZ:-72,maxZ:68});
export const WORLD_GROUND_RADIUS=76;
export function playableBounds(world) {
  const bounds=world.bounds??{minX:-90,maxX:90,minZ:-90,maxZ:90};
  return world.id==='valoria'?{minX:Math.max(bounds.minX,CITY_PLAYABLE_BOUNDS.minX),maxX:Math.min(bounds.maxX,CITY_PLAYABLE_BOUNDS.maxX),minZ:Math.max(bounds.minZ,CITY_PLAYABLE_BOUNDS.minZ),maxZ:Math.min(bounds.maxZ,CITY_PLAYABLE_BOUNDS.maxZ)}:bounds;
}
export function withinPlayableGround(point,world,margin=MOVEMENT.radius) {
  const b=playableBounds(world);
  return Number.isFinite(point?.x)&&Number.isFinite(point?.z)&&point.x>=b.minX+margin&&point.x<=b.maxX-margin&&point.z>=b.minZ+margin&&point.z<=b.maxZ-margin&&(world.id==='valoria'||Math.hypot(point.x,point.z)<=WORLD_GROUND_RADIUS-margin+1e-9);
}
// Flight is part of ordinary Valoria movement, including legacy authored worlds.
// Activity adapters retain their explicit policy (Snow Jam walking/riding).
export const flightAllowed=world=>!!world&&((world.id==='snowjam'||world.activity||world.authoring?.kind==='system-activity')?world.flightAllowed===true:true);
export function normalizeInput(input={}) {
  let moveX=clamp(finite(input.moveX),-1,1),moveZ=clamp(finite(input.moveZ),-1,1);
  const length=Math.hypot(moveX,moveZ); if(length>1) {moveX/=length;moveZ/=length;}
  // Readiness belongs to each immutable simulation sample, including retries/replay.
  const blocked=Array.isArray(input.blockedPortalWorldIds)?[...new Set(input.blockedPortalWorldIds.filter(id=>typeof id==='string'&&id.length>0&&id.length<=128))].slice(0,16):null;
  return {...(input.lookYaw!==undefined?{lookYaw:Math.atan2(Math.sin(finite(input.lookYaw)),Math.cos(finite(input.lookYaw)))}:{}),...(input.lookPitch!==undefined?{lookPitch:clamp(finite(input.lookPitch),-Math.PI/2,Math.PI/2)}:{}),...(blocked?{blockedPortalWorldIds:blocked}:{}),...(input.flightToggleSeq!==undefined?{flightToggleSeq:Number.isSafeInteger(input.flightToggleSeq)&&input.flightToggleSeq>0?input.flightToggleSeq:0}:{}),...(input.moveY!==undefined?{moveY:clamp(finite(input.moveY),-1,1)}:{}),moveX,moveZ,yaw:finite(input.yaw),jump:input.jump===true,sprint:input.sprint===true,seq:Number.isSafeInteger(input.seq)&&input.seq>=0?input.seq:0};
}
export function worldColliders(world) {
  const staticBoxes=(world.colliders??[]).map(c=>c.minX!==undefined?({...c}):({minX:c.x-c.width/2,maxX:c.x+c.width/2,minZ:c.z-c.depth/2,maxZ:c.z+c.depth/2,minY:c.minY??0,maxY:c.height??100}));
  for(const entity of world.entities??[]) {
    if(entity.kind==='model'){const p=entity.position,s=entity.scale,yaw=entity.rotation?.y??0,c=Math.cos(yaw),sn=Math.sin(yaw);for(const box of entity.colliders??[]){const x=box.center.x*s.x,z=box.center.z*s.z,cx=p.x+c*x+sn*z,cz=p.z-sn*x+c*z,cy=p.y+box.center.y*s.y,hx=(Math.abs(c)*box.size.x*s.x+Math.abs(sn)*box.size.z*s.z)/2,hz=(Math.abs(sn)*box.size.x*s.x+Math.abs(c)*box.size.z*s.z)/2,hy=box.size.y*s.y/2;staticBoxes.push({minX:cx-hx,maxX:cx+hx,minZ:cz-hz,maxZ:cz+hz,minY:cy-hy,maxY:cy+hy});}continue;}
    const cap=entity.capabilities?.find(c=>c.type==='collider');
    const topSurface=entity.kind==='island'||entity.kind==='bridge';
    if(!cap&&(!topSurface||entity.capabilities?.some(c=>c.type==='hover'))) continue;
    const p=entity.position,s=cap?.size??entity.scale;
    // Platforms use their authored Y as the walkable top, matching the renderer.
    // Other explicit colliders use centered axis-aligned bounds.
    const yaw=entity.rotation?.y??0,halfX=(Math.abs(Math.cos(yaw))*s.x+Math.abs(Math.sin(yaw))*s.z)/2,halfZ=(Math.abs(Math.sin(yaw))*s.x+Math.abs(Math.cos(yaw))*s.z)/2;
    staticBoxes.push({minX:p.x-halfX,maxX:p.x+halfX,minZ:p.z-halfZ,maxZ:p.z+halfZ,minY:topSurface?p.y-s.y:p.y-s.y/2,maxY:topSurface?p.y:p.y+s.y/2});
  }
  return staticBoxes;
}
const horizontal=(x,z,c,r)=>x+r>c.minX&&x-r<c.maxX&&z+r>c.minZ&&z-r<c.maxZ;
const vertical=(y,c,h)=>y+h>(c.minY??0)+0.001&&y<(c.maxY??100)-0.001;
// Each axis sweeps the entire footprint, including thin walls between endpoints.
const sweptHorizontal=(x,z,nextX,nextZ,c,r)=>Math.max(x,nextX)+r>c.minX&&Math.min(x,nextX)-r<c.maxX&&Math.max(z,nextZ)+r>c.minZ&&Math.min(z,nextZ)-r<c.maxZ;
// Integrate constant acceleration exactly, including the fraction of a step at
// target speed. This avoids frame-rate-dependent start/stop distances.
function approachVelocity(vx,vz,targetX,targetZ,acceleration,dt) {
  const dx=targetX-vx,dz=targetZ-vz,distance=Math.hypot(dx,dz);
  if(distance<1e-10)return {velocityX:targetX,velocityZ:targetZ,dx:targetX*dt,dz:targetZ*dt};
  const acceleratingTime=Math.min(dt,distance/acceleration),fraction=acceleration*acceleratingTime/distance;
  const velocityX=vx+dx*fraction,velocityZ=vz+dz*fraction,cruiseTime=dt-acceleratingTime;
  return {
    velocityX,velocityZ,
    dx:(vx+velocityX)*0.5*acceleratingTime+velocityX*cruiseTime,
    dz:(vz+velocityZ)*0.5*acceleratingTime+velocityZ*cruiseTime,
  };
}
function horizontalMotion(p,input,grounded,dt) {
  const inputLength=Math.hypot(input.moveX,input.moveZ),moving=inputLength>0;
  const speed=input.sprint?MOVEMENT.sprintSpeed:MOVEMENT.speed;
  const targetX=input.moveX*speed,targetZ=input.moveZ*speed;
  const acceleration=grounded?MOVEMENT.acceleration:MOVEMENT.airAcceleration;
  // Walking is direct control: releasing the stick/key stops at the current
  // position, rather than adding a braking slide. Air/flight retain momentum.
  // This shared rule also applies during authoritative replay and zero-dt clears.
  if(!moving&&grounded&&!p.flying)return {velocityX:0,velocityZ:0,dx:0,dz:0};
  if(!moving)return approachVelocity(p.velocityX,p.velocityZ,0,0,grounded?MOVEMENT.deceleration:MOVEMENT.airDeceleration,dt);
  if(grounded) {
    const forward=(p.velocityX*input.moveX+p.velocityZ*input.moveZ)/inputLength;
    const sideways=Math.abs(p.velocityX*input.moveZ-p.velocityZ*input.moveX)/inputLength;
    // Turn more firmly until within about 45 degrees of the desired direction.
    // Split exactly at that velocity-space boundary, rather than letting the
    // frame size decide when the acceleration rate switches.
    const turnBoundary=sideways*0.7/Math.sqrt(1-0.7**2);
    if(forward<turnBoundary-1e-10) {
      const fraction=(turnBoundary-forward)/(speed*inputLength-forward+turnBoundary);
      const turnTime=Math.min(dt,Math.hypot(targetX-p.velocityX,targetZ-p.velocityZ)*fraction/MOVEMENT.turnAcceleration);
      const turn=approachVelocity(p.velocityX,p.velocityZ,targetX,targetZ,MOVEMENT.turnAcceleration,turnTime);
      const after=approachVelocity(turn.velocityX,turn.velocityZ,targetX,targetZ,acceleration,dt-turnTime);
      return {...after,dx:turn.dx+after.dx,dz:turn.dz+after.dz};
    }
  }
  return approachVelocity(p.velocityX,p.velocityZ,targetX,targetZ,acceleration,dt);
}
/** Camera-relative pitch applies only to requested forward/back movement.
 * Strafe stays level; look alone never supplies thrust. Explicit ascent/descent
 * joins that direction before one 3D normalization and speed/acceleration cap.
 */
function flightMotion(p,input,dt) {
  const yaw=input.lookYaw??0,pitch=input.lookPitch??0,sin=Math.sin(yaw),cos=Math.cos(yaw);
  const forward=-(input.moveX*sin+input.moveZ*cos),tilt=forward*(Math.cos(pitch)-1);
  let x=input.moveX-tilt*sin,z=input.moveZ-tilt*cos,y=-forward*Math.sin(pitch)+(input.moveY??0)*MOVEMENT.flightVerticalSpeed/MOVEMENT.flightSpeed;
  const length=Math.hypot(x,y,z);if(length>1){x/=length;y/=length;z/=length;}
  const speed=input.sprint?MOVEMENT.flightBoostSpeed:MOVEMENT.flightSpeed;
  const tx=x*speed,ty=y*speed,tz=z*speed,dx=tx-p.velocityX,dy=ty-p.velocityY,dz=tz-p.velocityZ,distance=Math.hypot(dx,dy,dz);
  if(distance<1e-10)return {velocityX:tx,velocityY:ty,velocityZ:tz,dx:tx*dt,dy:ty*dt,dz:tz*dt};
  const acceleration=length>0?MOVEMENT.flightAcceleration:MOVEMENT.flightDeceleration;
  const acceleratingTime=Math.min(dt,distance/acceleration),fraction=acceleration*acceleratingTime/distance,cruiseTime=dt-acceleratingTime;
  const velocityX=p.velocityX+dx*fraction,velocityY=p.velocityY+dy*fraction,velocityZ=p.velocityZ+dz*fraction;
  return {velocityX,velocityY,velocityZ,dx:(p.velocityX+velocityX)*.5*acceleratingTime+velocityX*cruiseTime,dy:(p.velocityY+velocityY)*.5*acceleratingTime+velocityY*cruiseTime,dz:(p.velocityZ+velocityZ)*.5*acceleratingTime+velocityZ*cruiseTime};
}
export function stepMovement(player,rawInput,elapsed,world) {
  const input=normalizeInput(rawInput),dt=clamp(finite(elapsed),0,MOVEMENT.maxDt);
  const p={...player,position:{...player.position},velocityX:finite(player.velocityX),velocityY:finite(player.velocityY),velocityZ:finite(player.velocityZ),yaw:input.yaw};
  p.coyoteRemaining=clamp(finite(player.coyoteRemaining),0,MOVEMENT.coyoteTime);
  p.jumpBufferRemaining=input.jump&&!player.jumpHeld?MOVEMENT.jumpBufferTime:clamp(finite(player.jumpBufferRemaining),0,MOVEMENT.jumpBufferTime);
  p.jumpHeld=input.jump;
  p.flying=flightAllowed(world)&&player.flying===true;
  p.lastFlightToggleSeq=Math.max(0,Number.isSafeInteger(player.lastFlightToggleSeq)?player.lastFlightToggleSeq:0);
  if(input.flightToggleSeq>p.lastFlightToggleSeq&&input.flightToggleSeq<=input.seq) {
    p.lastFlightToggleSeq=input.flightToggleSeq;
    if(flightAllowed(world)) {p.flying=!p.flying;p.velocityY=0;}
    p.jumpBufferRemaining=0;p.coyoteRemaining=0;
  }
  // Clamp after permission/toggle resolution. Boost release eases down instead
  // of snapping; leaving flight restores the unchanged ordinary movement cap.
  const initialSpeed=p.flying?Math.hypot(p.velocityX,p.velocityY,p.velocityZ):Math.hypot(p.velocityX,p.velocityZ),maxSpeed=p.flying?MOVEMENT.flightBoostSpeed:MOVEMENT.sprintSpeed;
  if(initialSpeed>maxSpeed) {p.velocityX*=maxSpeed/initialSpeed;p.velocityZ*=maxSpeed/initialSpeed;if(p.flying)p.velocityY*=maxSpeed/initialSpeed;}
  if(p.flying){p.jumpBufferRemaining=0;p.coyoteRemaining=0;}
  const bounds=playableBounds(world);
  const colliders=worldColliders(world),gravity=clamp(finite(world.gravity,18),1,40),r=MOVEMENT.radius,h=MOVEMENT.height;
  // A rising avatar near the floor is not supported and cannot refresh its jump.
  const supported=()=>p.velocityY<=0&&(p.position.y<=0.001||colliders.some(c=>horizontal(p.position.x,p.position.z,c,r)&&Math.abs(p.position.y-(c.maxY??100))<0.025));
  const steps=Math.max(1,Math.ceil(dt/MOVEMENT.maxStep)),step=dt/steps;
  for(let n=0;n<steps;n++) {
    let grounded=!p.flying&&supported();
    if(grounded)p.coyoteRemaining=MOVEMENT.coyoteTime;
    if(p.jumpBufferRemaining>0&&(grounded||p.coyoteRemaining>0)) {
      p.velocityY=MOVEMENT.jumpSpeed;
      p.jumpBufferRemaining=0;p.coyoteRemaining=0;grounded=false;
    }
    const motion=p.flying?flightMotion(p,input,step):horizontalMotion(p,input,grounded,step);
    p.velocityX=motion.velocityX;p.velocityZ=motion.velocityZ;
    const stepBaseY=p.position.y;
    const moveAxis=(nextX,nextZ)=>{
      const {x,y,z}=p.position;
      if(nextX===x&&nextZ===z)return true;
      const swept=c=>sweptHorizontal(x,z,nextX,nextZ,c,r);
      const blockers=colliders.filter(c=>vertical(y,c,h)&&swept(c));
      if(!blockers.length)return true;
      // Walking may lift the feet onto a curb, never turn a jump or fall into
      // climbing. Limit the total rise across both axes of this substep.
      if(!grounded||!supported())return false;
      const top=Math.max(...blockers.map(c=>c.maxY??100));
      if(top<=y+0.001||top>stepBaseY+MOVEMENT.stepHeight+1e-9)return false;
      // Raise at the old position, sweep at the new height, then require a
      // real supporting top at the destination. Check the whole upward body
      // sweep so a low ceiling cannot be bypassed by stepping through it.
      if(colliders.some(c=>horizontal(x,z,c,r)&&y<(c.maxY??100)-0.001&&top+h>(c.minY??0)+0.001))return false;
      if(colliders.some(c=>vertical(top,c,h)&&swept(c)))return false;
      if(!colliders.some(c=>Math.abs((c.maxY??100)-top)<0.001&&horizontal(nextX,nextZ,c,r)))return false;
      p.position.y=top;p.velocityY=0;
      return true;
    };
    const requestedX=p.position.x+motion.dx,circleX=world.id==='valoria'?Infinity:Math.sqrt(Math.max(0,(WORLD_GROUND_RADIUS-r)**2-p.position.z**2)),nextX=clamp(requestedX,Math.max(bounds.minX+r,-circleX),Math.min(bounds.maxX-r,circleX));
    if(moveAxis(nextX,p.position.z)) {
      p.position.x=nextX;if(nextX!==requestedX)p.velocityX=0;
    } else p.velocityX=0;
    const requestedZ=p.position.z+motion.dz,circleZ=world.id==='valoria'?Infinity:Math.sqrt(Math.max(0,(WORLD_GROUND_RADIUS-r)**2-p.position.x**2)),nextZ=clamp(requestedZ,Math.max(bounds.minZ+r,-circleZ),Math.min(bounds.maxZ-r,circleZ));
    if(moveAxis(p.position.x,nextZ)) {
      p.position.z=nextZ;if(nextZ!==requestedZ)p.velocityZ=0;
    } else p.velocityZ=0;
    // Follow small descending treads while walking. A real ledge drop stays
    // airborne; jump/coyote input has already cleared grounded above.
    if(step>0&&grounded&&p.velocityY<=0) {
      let support=0;
      for(const c of colliders)if(horizontal(p.position.x,p.position.z,c,r)&&(c.maxY??100)<=p.position.y+0.001) support=Math.max(support,c.maxY??100);
      const drop=p.position.y-support;
      if(drop>0&&drop<=MOVEMENT.groundSnap+1e-9&&!colliders.some(c=>horizontal(p.position.x,p.position.z,c,r)&&support<(c.maxY??100)-0.001&&p.position.y+h>(c.minY??0)+0.001)) {
        p.position.y=support;p.velocityY=0;
      }
    }
    let nextY;
    if(p.flying) {
      p.velocityY=motion.velocityY;nextY=p.position.y+motion.dy;
      if(nextY>MOVEMENT.flightMaxHeight){nextY=MOVEMENT.flightMaxHeight;p.velocityY=0;}
    } else {p.velocityY-=gravity*step;nextY=p.position.y+p.velocityY*step;}
    for(const c of colliders) if(horizontal(p.position.x,p.position.z,c,r)) {
      const top=c.maxY??100,bottom=c.minY??0;
      if(nextY<=p.position.y&&p.position.y>=top-0.025&&nextY<top) {nextY=top;p.velocityY=0;}
      else if(nextY>p.position.y&&p.position.y+h<=bottom+0.001&&nextY+h>bottom) {nextY=bottom-h;p.velocityY=0;}
    }
    if(nextY<0) {nextY=0;p.velocityY=0;} p.position.y=nextY;
    p.coyoteRemaining=Math.max(0,p.coyoteRemaining-step);
    p.jumpBufferRemaining=Math.max(0,p.jumpBufferRemaining-step);
  }
  p.grounded=!p.flying&&supported();
  p.lastInputSeq=input.seq;
  return p;
}
